import { Injectable } from '@angular/core';
import {
  getAllInvoicesPageViewModel,
  getFiltersAndSortInformation,
  getSelectedPaymentCustomers,
} from '@app/payments/selectors/payments.selector';
import { Store } from '@ngrx/store';
import { PanAppState, SESSION_STORAGE_KEYS } from '@panamax/app-state';
import { ModalFilterService } from '@payments/services/payments-modal.service';
import { FEATURES } from '@shared/constants/splitio-features';
import {
  Filter,
  InvoiceFilterIds,
  InvoiceStoreService,
  SortDirection,
  SortFilter,
} from '@usf/ngrx-payment';
import { SortFields, Status } from '@usf/payment-types';
import { Observable, combineLatest, of } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';

import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { FileHandlerPaymentsService } from '@payments/services/file-handler-payments.service';
import { PaymentsService } from '@payments/services/payments.service';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { PaymentTransactionsViewModel } from '@usf/payment-types';
import { AllInvoicesPageVM } from '../all-invoices.page';
import { AllInvoicesAnalyticsService } from '../tracking/analytics/all-invoices-analytics.service';

@Injectable({
  providedIn: 'root',
})
export class AllInvoicesService implements ModalFilterService {
  constructor(
    private store: Store,
    private invoiceStoreService: InvoiceStoreService,
    private allInvoicesAnalytics: AllInvoicesAnalyticsService,
    private paymentsService: PaymentsService,
    private panAppState: PanAppState,
    readonly platform: PlatformService,
    private popoverController: PopoverController,
    private actionSheetController: ActionSheetController,
    private fileHandlerService: FileHandlerPaymentsService,
  ) {}

  trackPageLoad(): void {
    this.allInvoicesAnalytics.trackPageLoad();
  }

  getInvoices$(): Observable<AllInvoicesPageVM> {
    return combineLatest([
      this.store.select(getAllInvoicesPageViewModel),
      this.panAppState.feature$([FEATURES.split_global_payments_outage.name]),
    ]).pipe(
      map(([vm, paymentHasOutage]) => {
        return {
          ...vm,
          paymentHasOutage,
        };
      }),
    );
  }

  getFiltersAndSortInformation$(): Observable<{
    filters: Filter[];
    sort: SortFilter[];
  }> {
    return this.store.select(getFiltersAndSortInformation);
  }

  getStatusOptions(): Map<Status, string> {
    return new Map([
      [Status.OVERDUE, 'Overdue'],
      [Status.PARTIALLY_PAID, 'Partially Paid'],
      [Status.UNPAID, 'Unpaid'],
      [Status.UNUSED, 'Unused'],
      [Status.APPLIED, 'Applied'],
      [Status.PAID, 'Paid'],
      [Status.PENDING, 'Pending'],
      [Status.AUTOPAY, 'Autopay'],
    ]);
  }

  setInitialSortAndFilter(initialFilters: Filter[] = []) {
    const ninetyDays = 1000 * 60 * 60 * 24 * 89;
    const now = new Date();
    const todaysDate = this.formatDate(now);
    const ninetyDaysAgo = this.formatDate(new Date(now.valueOf() - ninetyDays));

    const invoiceFilters =
      initialFilters.length > 0
        ? initialFilters
        : [
            {
              filterName: InvoiceFilterIds.FromIssueDate,
              value: ninetyDaysAgo,
            },
            { filterName: InvoiceFilterIds.ToIssueDate, value: todaysDate },
          ];

    const includeCustomerFilter =
      invoiceFilters.findIndex(
        filter => filter.filterName === InvoiceFilterIds.Customers,
      ) >= 0;
    if (includeCustomerFilter) {
      this.invoiceStoreService.setInvoiceFilters(invoiceFilters);
      this.invoiceStoreService.setInvoiceSort([
        { fieldName: SortFields.issueDate, sortValue: SortDirection.DESC },
        { fieldName: SortFields.transaction, sortValue: SortDirection.DESC },
      ]);
      return of(null);
    } else {
      return this.getPaymentSelectedCustomers().pipe(
        first(paymentCustomers => paymentCustomers.customers.length > 0),
        tap(paymentCustomers => {
          const invoiceFiltersWithCustomers = [
            ...invoiceFilters,
            {
              filterName: InvoiceFilterIds.Customers,
              value: paymentCustomers.customers,
            } as Filter,
          ];
          this.invoiceStoreService.setInvoiceFilters(
            invoiceFiltersWithCustomers,
          );
          this.invoiceStoreService.setInvoiceSort([
            { fieldName: SortFields.issueDate, sortValue: SortDirection.DESC },
            {
              fieldName: SortFields.transaction,
              sortValue: SortDirection.DESC,
            },
          ]);
        }),
      );
    }
  }

  clearFiltersAndResetSort() {
    this.invoiceStoreService.setInvoiceFilters([]);
    this.invoiceStoreService.setInvoiceSort([
      { fieldName: SortFields.issueDate, sortValue: SortDirection.DESC },
      { fieldName: SortFields.transaction, sortValue: SortDirection.DESC },
    ]);
  }

  setFilter(invoiceFilter: Filter[]) {
    this.invoiceStoreService.setInvoiceFilters(invoiceFilter);
  }

  setSort(sortOrder: SortFilter[]) {
    this.invoiceStoreService.setInvoiceSort(sortOrder);
  }

  formatDate(date: Date): number {
    return +date.toJSON().replace(/-/g, '').substring(0, 8);
  }

  getPaymentSelectedCustomers() {
    return this.store.select(getSelectedPaymentCustomers);
  }

  showPaymentsOutageMessageIfNeeded(
    paymentHasOutage: boolean,
    outageMessage: string,
  ) {
    if (
      paymentHasOutage &&
      !sessionStorage.getItem(
        SESSION_STORAGE_KEYS.paymentsOutageMessageDismissed_AllInvoicePage,
      )
    ) {
      this.paymentsService.showToastMessage(
        outageMessage,
        true,
        MessageTypeEnum.warning,
      );
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.paymentsOutageMessageDismissed_AllInvoicePage,
        'true',
      );
    }
  }

  async presentPaymentOptions(buttons: any[], event?) {
    if (this.platform.platformType === PlatformEnum.desktop) {
      buttons.pop();
      const popover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        cssClass: 'ellipsis-options-popover-desktop',
        componentProps: {
          buttons,
        },
        mode: 'ios',
        event,
        showBackdrop: false,
      });
      return await popover.present();
    } else {
      const actionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons: buttons,
      });
      await actionSheet.present();
    }
  }

  downloadAllInvoices(transactions: PaymentTransactionsViewModel[]) {
    this.fileHandlerService.downloadAllInvoices('All Invoices', transactions);
  }
}
